// ─── Sentry must be imported BEFORE everything else ─────────────────────────
import * as Sentry from "@sentry/react";

// Silence non-error console output in production builds (avoid leaking internals)
if (process.env.NODE_ENV === "production") {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.table = noop;
  console.trace = noop;
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENVIRONMENT || "development",
  // 10 % sampling in prod, 100 % elsewhere
  tracesSampleRate: process.env.REACT_APP_ENVIRONMENT === "production" ? 0.1 : 1.0,
  // Replay 1 % of sessions; 100 % when an error occurs
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  // Replay (rrweb, ~40 KB gz) is added lazily below so it stays out of the critical bundle.
  integrations: [],
  // Don't report browser extension noise
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "Non-Error promise rejection captured",
    "chrome-extension://",
    "moz-extension://",
  ],
});

const deferred = (fn, timeout) =>
  ("requestIdleCallback" in window ? window.requestIdleCallback(fn, { timeout }) : setTimeout(fn, timeout));

deferred(() => {
  import(/* webpackChunkName: "sentry-replay" */ "@sentry/react")
    .then((mod) => Sentry.addIntegration(mod.replayIntegration({ maskAllText: false, blockAllMedia: false })))
    .catch(() => {});
}, 3000);

// Restore user context for already-logged-in sessions
try {
  const slug = localStorage.getItem('organizationSlug') || localStorage.getItem('orgSlug');
  const orgId = localStorage.getItem('organizationId');
  if (slug) {
    Sentry.setUser({ username: slug, id: orgId || undefined });
    Sentry.setTag('org_slug', slug);
  }
} catch (_) {}
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import ReactDOM from "react-dom/client";
import { SWRConfig } from "swr";
import "@/index.css";
import App from "@/App";
// Clerk disabled - using custom JWT auth instead
// import { ClerkProvider } from '@clerk/clerk-react';

// Clerk disabled - using custom JWT auth
// const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// PostHog (EU cloud) — ~80 KB gz, analytics only: loaded after first paint so it never
// competes with the page. capture_pageview 'history_change' still records the initial view.
if (process.env.REACT_APP_POSTHOG_KEY && process.env.REACT_APP_POSTHOG_HOST) {
  deferred(() => {
    import(/* webpackChunkName: "posthog" */ 'posthog-js').then(({ default: posthog }) => {
      posthog.init(
        process.env.REACT_APP_POSTHOG_KEY,
        {
          api_host: process.env.REACT_APP_POSTHOG_HOST,
          defaults: '2026-05-30',
          person_profiles: 'identified_only',
          capture_pageview: 'history_change',
          capture_heatmaps: true,
          autocapture: true,
          secure_cookie: window.location.protocol === 'https:',
          loaded: (ph) => {
            // Link Sentry errors to PostHog session replays/analytics
            try {
              Sentry.getCurrentScope().setTag('posthog_session_id', ph.get_session_id());
            } catch (e) { /* sentry optional */ }
          },
        }
      );
    }).catch(() => {});
  }, 2500);
}

// Register Service Worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        
        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              }
            });
          }
        });
      })
      .catch((error) => {
      });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
      <SWRConfig
        value={{
          revalidateOnFocus: true,
          revalidateOnReconnect: true,
          focusThrottleInterval: 10000,
          dedupingInterval: 5000,
          errorRetryCount: 2,
        }}
      >
        <App />
      </SWRConfig>
  </React.StrictMode>,
);
